import { UpdateSlotBodyScope } from '@api/models';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';

import { useToast } from '@ui/components/common/toast';
import { useApiErrorMessage } from '@website/hooks/useApiErrorMessage';
import { ApiClient } from '@website/lib/api';
import { Slot, getSlotById, updateSlot as updateSlotService } from '@website/services/slotManagement.service';

interface ToastMessages {
  successTitle?: string;
  successDescription?: string;
  errorTitle: string;
  errorDescription: string;
}

interface SlotOccurrenceIdentifier {
  slotId: string;
  startTime: number; // Original UTC time in minutes
  endTime: number; // Original UTC time in minutes
}

interface SlotOccurrenceDeleteData extends SlotOccurrenceIdentifier {
  deleteRecursively?: boolean;
  date?: string; // Required for single-date delete
  /** "This and following" pivot — removal starts here, earlier dates are kept. */
  fromDate?: string;
}

interface SlotOccurrenceUpdateData extends SlotOccurrenceIdentifier {
  newStartTime?: number;
  newEndTime?: number;
  newPrice?: number;
  updateRecursively?: boolean;
  date?: string; // Required for single-date update
  /** Move the occurrence/series to a new day (YYYY-MM-DD). */
  newDate?: string;
  /** "This and following" pivot — turns a recursive update into a split at this date. */
  fromDate?: string;
  /** How future bookings are handled on a series/this-and-following date move. */
  bookedStrategy?: 'move' | 'leave';
  newPaymentMethods?: ('cash' | 'online')[]; // Payment methods override (occurrence-level for single-date, rule-level for recursive)
  newFirstBookingOnline?: boolean;
}

/**
 * Cancel a single slot occurrence by materializing a CANCELLED child slot
 */
async function cancelSlotOccurrence(params: { slotId: string; date: string; startTime: number; endTime: number }) {
  try {
    const result = await ApiClient.cancelSlotOccurrence({
      slotId: params.slotId,
      date: params.date,
      startTime: params.startTime,
      endTime: params.endTime
    });
    return result;
  } catch (error) {
    console.error('Failed to cancel slot:', error);
    throw new Error('Failed to cancel slot. Please try again.');
  }
}

/**
 * Update a single slot occurrence by materializing/updating an overridden child slot
 */
async function updateSlotOccurrence(params: {
  slotId: string;
  date: string;
  startTime: number;
  endTime: number;
  newDate?: string;
  newStartTime?: number;
  newEndTime?: number;
  newPrice?: number;
  newAcceptsCash?: boolean;
  newAcceptsOnline?: boolean;
  newFirstBookingOnline?: boolean;
}) {
  try {
    // Single-occurrence edit via the unified endpoint (scope: UpdateSlotBodyScope.occurrence).
    // `anchorDate` (when set) relocates the occurrence to a new day.
    const result = await ApiClient.updateSlot(params.slotId, {
      scope: UpdateSlotBodyScope.occurrence,
      occurrenceDate: params.date,
      updates: {
        ...(params.newDate !== undefined ? { anchorDate: params.newDate } : {}),
        ...(params.newStartTime !== undefined ? { startTime: params.newStartTime } : {}),
        ...(params.newEndTime !== undefined ? { endTime: params.newEndTime } : {}),
        ...(params.newPrice !== undefined ? { price: params.newPrice } : {}),
        ...(params.newAcceptsCash !== undefined ? { acceptsCash: params.newAcceptsCash } : {}),
        ...(params.newAcceptsOnline !== undefined ? { acceptsOnline: params.newAcceptsOnline } : {}),
        ...(params.newFirstBookingOnline !== undefined ? { firstBookingOnline: params.newFirstBookingOnline } : {})
      }
    });
    return result;
  } catch (error) {
    // Preserve the API's reason (e.g. "This occurrence has a booking and cannot
    // be moved") so the caller's onError can surface it, instead of a generic copy.
    console.error('Failed to update slot:', error);
    throw error instanceof Error ? error : new Error(String(error));
  }
}

/**
 * Hook for deleting a slot occurrence.
 * - Single delete: Materializes a CANCELLED child for that specific date
 * - Recursive delete: Removes the slot rule going forward (past/booked occurrences left unchanged)
 */
export function useSlotOccurrenceDelete(toastMessages?: ToastMessages) {
  const queryClient = useQueryClient();
  const router = useRouter();
  const { success, error } = useToast();

  return useMutation<
    {
      deleted: 'slot-single' | 'slot-recursive' | 'slot';
      slot?: Slot | null;
      /** Booked occurrences preserved by a recursive removal (never cancelled). */
      preservedBookings?: number;
    },
    Error,
    { slot: SlotOccurrenceDeleteData; locale?: string; skipSuccessToast?: boolean }
  >({
    mutationFn: async ({ slot }) => {
      // Single-date delete: Materialize a CANCELLED child for this specific date
      if (!slot.deleteRecursively && slot.date) {
        await cancelSlotOccurrence({
          slotId: slot.slotId,
          date: slot.date,
          startTime: slot.startTime,
          endTime: slot.endTime
        });
        return { deleted: 'slot-single' };
      }

      // Recursive delete: remove the rule going forward. `fromDate` (when set)
      // makes it "this and following" — earlier dates are kept.
      const result = await ApiClient.removeTimeslotRecursively({
        slotId: slot.slotId,
        ...(slot.fromDate ? { fromDate: slot.fromDate } : {})
      });
      return {
        deleted: result.ruleDeleted ? 'slot' : 'slot-recursive',
        slot: null,
        preservedBookings: result.preservedBookings
      };
    },
    onSuccess: (result, variables) => {
      // Skip showing toast if caller wants to show a custom one
      if (!variables.skipSuccessToast) {
        if (result.deleted === 'slot') {
          success(
            toastMessages?.successTitle || 'Slot Deleted',
            toastMessages?.successDescription || 'The slot has been deleted as it had no remaining occurrences.'
          );
        } else if (result.deleted === 'slot-single') {
          success(
            toastMessages?.successTitle || 'Slot Cancelled',
            toastMessages?.successDescription || 'The slot has been cancelled for this date.'
          );
        } else {
          success(
            toastMessages?.successTitle || 'Slot Deleted',
            toastMessages?.successDescription || 'The slot has been removed from all dates.'
          );
        }
      }

      // Remove cumulative cache completely to avoid stale merged data
      queryClient.removeQueries({ queryKey: ['available-slots-all'] });

      // Invalidate all relevant queries
      queryClient.invalidateQueries({ queryKey: ['available-slots'] });
      queryClient.invalidateQueries({ queryKey: ['activity'] });
      queryClient.invalidateQueries({ queryKey: ['professional-activities'] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['user-activities'] });
      queryClient.invalidateQueries({ queryKey: ['bookings'] });

      router.refresh();
    },
    onError: (err) => {
      // Surface the API's reason (e.g. "This occurrence has a booking and cannot
      // be removed") without the transport prefix; fall back to the generic copy.
      const reason = (err.message || '').replace(/^HTTP \d+:\s*/, '');
      error(
        toastMessages?.errorTitle || 'Error',
        reason || toastMessages?.errorDescription || 'Failed to delete slot. Please try again.'
      );
    }
  });
}

/**
 * Hook for updating a slot occurrence.
 * Can update the time range and/or price of a specific occurrence.
 *
 * - If updateRecursively is false and date is provided: Materializes/updates an overridden child for that date only
 * - If updateRecursively is true: Updates the slot rule (affects all dates)
 */
export function useSlotOccurrenceUpdate(toastMessages?: ToastMessages) {
  const queryClient = useQueryClient();
  const router = useRouter();
  const { error } = useToast();
  const apiErrorMessage = useApiErrorMessage();

  return useMutation<Slot | null, Error, { slot: SlotOccurrenceUpdateData; locale?: string }>({
    mutationFn: async ({ slot }) => {
      // Single-date update: Materialize/update an overridden child for this specific date
      // (or relocate it to `newDate`).
      if (!slot.updateRecursively && slot.date) {
        await updateSlotOccurrence({
          slotId: slot.slotId,
          date: slot.date,
          startTime: slot.startTime,
          endTime: slot.endTime,
          newDate: slot.newDate,
          newStartTime: slot.newStartTime,
          newEndTime: slot.newEndTime,
          newPrice: slot.newPrice,
          newAcceptsCash: slot.newPaymentMethods ? slot.newPaymentMethods.includes('cash') : undefined,
          newAcceptsOnline: slot.newPaymentMethods ? slot.newPaymentMethods.includes('online') : undefined,
          newFirstBookingOnline: slot.newFirstBookingOnline
        });
        return null; // Return null since we're not updating the rule
      }

      // Recursive update: Modify the slot rule (whole series, or "this and
      // following" when `fromDate` is set). `newDate` moves the series start.
      const existing = await getSlotById(slot.slotId);

      if (!existing) {
        throw new Error('Slot not found');
      }

      const updatedSlot = await updateSlotService(
        slot.slotId,
        {
          startTime: slot.newStartTime ?? existing.startTime,
          endTime: slot.newEndTime ?? existing.endTime,
          price: slot.newPrice ?? existing.price,
          ...(slot.newDate ? { anchorDate: slot.newDate } : {}),
          ...(slot.newPaymentMethods && {
            acceptsCash: slot.newPaymentMethods.includes('cash'),
            acceptsOnline: slot.newPaymentMethods.includes('online')
          }),
          ...(slot.newFirstBookingOnline !== undefined ? { firstBookingOnline: slot.newFirstBookingOnline } : {})
        },
        slot.fromDate,
        slot.bookedStrategy
      );

      return updatedSlot;
    },
    onSuccess: () => {
      // Success toast intentionally suppressed — the edit is reflected in the
      // calendar/list directly; only failures surface a toast.
      // Remove cumulative cache completely to avoid stale merged data
      queryClient.removeQueries({ queryKey: ['available-slots-all'] });

      // Invalidate all relevant queries
      queryClient.invalidateQueries({ queryKey: ['available-slots'] });
      queryClient.invalidateQueries({ queryKey: ['activity'] });
      queryClient.invalidateQueries({ queryKey: ['professional-activities'] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['user-activities'] });
      queryClient.invalidateQueries({ queryKey: ['bookings'] });

      router.refresh();
    },
    onError: (err) => {
      // Prefer the API's business-rule reason over the generic fallback so a
      // blocked occurrence edit (booked move, range too short) explains itself.
      error(
        toastMessages?.errorTitle || 'Error',
        apiErrorMessage(err, toastMessages?.errorDescription || 'Failed to update slot. Please try again.', {
          serverReason: true
        })
      );
    }
  });
}
